import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle, Info, XCircle } from 'lucide-react';

type AlertVariant = 'info' | 'success' | 'warning' | 'error';

interface AlertProps {
  variant?: AlertVariant;
  title?: string;
  children: ReactNode;
  icon?: ReactNode;
  className?: string;
}

const variantStyles = {
  info: {
    container: 'bg-primary/10 border-primary/20',
    icon: 'text-primary',
    title: 'text-primary',
    text: 'text-primary/80',
    defaultIcon: Info,
  },
  success: {
    container: 'bg-success/10 border-success/20',
    icon: 'text-success',
    title: 'text-success',
    text: 'text-success/80',
    defaultIcon: CheckCircle,
  },
  warning: {
    container: 'bg-warning/10 border-warning/20',
    icon: 'text-warning',
    title: 'text-warning',
    text: 'text-warning/80',
    defaultIcon: AlertTriangle,
  },
  error: {
    container: 'bg-error/10 border-error/20',
    icon: 'text-error',
    title: 'text-error',
    text: 'text-error/80',
    defaultIcon: XCircle,
  },
};

export function Alert({
  variant = 'info',
  title,
  children,
  icon,
  className = '',
}: AlertProps) {
  const styles = variantStyles[variant];
  const IconComponent = styles.defaultIcon;

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-lg border
        ${styles.container}
        ${className}
      `}
      role="alert"
    >
      <div className={`flex-shrink-0 mt-0.5 ${styles.icon}`}>
        {icon || <IconComponent className="w-5 h-5" />}
      </div>
      <div className="flex-1 min-w-0">
        {title && (
          <p className={`text-sm font-medium ${styles.title}`}>{title}</p>
        )}
        <div className={`text-sm ${title ? 'mt-1' : ''} ${styles.text}`}>
          {children}
        </div>
      </div>
    </div>
  );
}
